import { createContext, useContext } from 'react';

import { Table } from '@tanstack/react-table';

import { Button } from '@components/button';
import { IconGoogleSheets } from '@components/icons/brand-google-sheets';
import { IconDownload } from '@components/icons/download';
import { IconMoreVertical } from '@components/icons/more-vertical';
import { IconOpenNewTab } from '@components/icons/open-new-tab';
import { IconTrash } from '@components/icons/trash';
import { Menu } from '@components/menu';
import { Stack } from '@components/stack';
import { useScopedTranslate } from '@locale/translate';
import { downloadAsCsv } from '@utils/browser';
import { pxToRem } from '@utils/converters';

import { accessorMap } from './renderers';
import { sortByReferenceArray } from './sorters';
import { AvailableColumn, ColumnType, DataItemType } from './types';

import { IconCsvFile } from '../icons/csv-file';
import { IconXlsFile } from '../icons/xls-file';

export type ExportConverterProps = {
  convertToExcel?: (csvContent: string) => Promise<void> | void;
  convertToSheets?: (csvContent: string) => Promise<string | void> | string | void;
  connectSheets?: () => Promise<void> | void;
  disconnectSheets?: () => void;
};

export const ExportConvertersContext = createContext<ExportConverterProps | null>(null);

export const useExportConverters = () => useContext(ExportConvertersContext);

type ExportBaseProps<T extends DataItemType> = {
  availableColumns: Array<AvailableColumn<T>>;
  table: Table<T>;
};

const useExportActions = <T extends DataItemType>({
  availableColumns,
  table,
}: ExportBaseProps<T>) => {
  const { t } = useScopedTranslate(locale);
  const converters = useExportConverters();

  const convertToExcel = converters?.convertToExcel;
  const convertToSheets = converters?.convertToSheets;
  const connectSheets = converters?.connectSheets;
  const disconnectSheets = converters?.disconnectSheets;

  const hasConverters = Boolean(convertToExcel || convertToSheets || connectSheets);

  const getCsvContent = () => {
    return buildCsvContent(availableColumns, table);
  };

  const exportCsv = () => {
    downloadAsCsv({ content: getCsvContent() });
  };

  const exportExcel = async () => {
    if (!convertToExcel) return;
    await convertToExcel(getCsvContent());
  };

  const exportGoogleSheets = async () => {
    if (connectSheets) {
      await connectSheets();
      return;
    }
    if (convertToSheets) {
      const url = await convertToSheets(getCsvContent());
      if (typeof url === 'string') {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
    }
  };

  return {
    t,
    hasConverters,
    exportCsv,
    exportExcel,
    exportGoogleSheets,
    convertToExcel,
    convertToSheets,
    connectSheets,
    disconnectSheets,
  };
};

const stopEvent = (e: React.SyntheticEvent) => {
  e.preventDefault();
  e.stopPropagation();
};

const ExportMenuItems = ({
  exportCsv,
  exportExcel,
  exportGoogleSheets,
  convertToExcel,
  convertToSheets,
  connectSheets,
  disconnectSheets,
  t,
}: ReturnType<typeof useExportActions>) => {
  return (
    <>
      <Menu.Item
        label={t('csv')}
        onSelect={exportCsv}
        icon={<IconCsvFile color="elementPurple80" />}
        isSubmenu
      />
      {convertToExcel && (
        <Menu.Item
          label={t('xls')}
          onSelect={exportExcel}
          icon={<IconXlsFile color="elementPurple80" />}
          isSubmenu
        />
      )}
      {(convertToSheets || connectSheets) && (
        <>
          <Menu.Separator />
          {connectSheets ? (
            <Menu.Item
              label={t('googleSheets')}
              icon={<IconGoogleSheets />}
              isSubmenu
              onSelect={connectSheets}
              iconRight={
                <Button size="xs" variant="primary-blue" font="semibold12" text={t('connect')} />
              }
            />
          ) : (
            <Menu.Item
              label={
                <Stack orientation="horizontal" space={1} align="center">
                  {t('googleSheets')}
                  <IconOpenNewTab size={16} color="elementPurple50" />
                </Stack>
              }
              icon={<IconGoogleSheets />}
              isSubmenu
              onSelect={exportGoogleSheets}
              iconRight={
                disconnectSheets && (
                  <div onPointerDown={stopEvent} onMouseUp={stopEvent}>
                    <Menu
                      placement="bottom-start"
                      trigger="div"
                      triggerProps={{
                        style: { padding: pxToRem(4), cursor: 'pointer', display: 'flex' },
                        children: <IconMoreVertical size={16} color="elementPurple50" />,
                      }}
                    >
                      <Menu.Item
                        label={t('disconnect')}
                        icon={<IconTrash />}
                        variant="danger"
                        onSelect={disconnectSheets}
                      />
                    </Menu>
                  </div>
                )
              }
            />
          )}
        </>
      )}
    </>
  );
};

export const ExportCsvButton = <T extends DataItemType>(props: ExportBaseProps<T>) => {
  const actions = useExportActions(props);
  const { t, hasConverters, exportCsv } = actions;

  if (hasConverters) {
    return (
      <Menu
        placement="bottom-start"
        buttonProps={{
          size: 'sm',
          iconLeft: <IconDownload />,
          variant: 'secondary',
          text: t('export'),
        }}
      >
        <ExportMenuItems {...actions} />
      </Menu>
    );
  }

  return (
    <Button
      size="sm"
      iconLeft={<IconDownload />}
      variant="secondary"
      text={t('exportCsv')}
      onClick={exportCsv}
    />
  );
};

export const ExportCsvMenuItem = <T extends DataItemType>(props: ExportBaseProps<T>) => {
  const actions = useExportActions(props);
  const { t, hasConverters, exportCsv } = actions;

  if (hasConverters) {
    return (
      <Menu.Submenu label={t('export')} icon={<IconDownload />} width={pxToRem(280)}>
        <ExportMenuItems {...actions} />
      </Menu.Submenu>
    );
  }

  return <Menu.Item icon={<IconDownload />} label={t('exportCsv')} onSelect={exportCsv} />;
};

const locale = {
  en: {
    export: 'Export',
    exportCsv: 'Export to CSV',
    csv: 'CSV',
    xls: 'XLS',
    googleSheets: 'Google Sheets',
    connect: 'Connect',
    disconnect: 'Disconnect',
  },
  is: {
    export: 'Sækja',
    exportCsv: 'Sækja sem CSV',
    csv: 'CSV',
    xls: 'XLS',
    googleSheets: 'Google Sheets',
    connect: 'Tengja',
    disconnect: 'Aftengja',
  },
};

export const DANGEROUS_START_CHARACTERS = ['=', '+', '-', '@'];

export const buildCsvField = (value: string): string => {
  const guarded = DANGEROUS_START_CHARACTERS.some((char) => {
    return value.startsWith(char);
  })
    ? `'${value}`
    : value;
  return /[",;\n\r]/.test(guarded) ? `"${guarded.replace(/"/g, '""')}"` : guarded;
};

const buildCsvContent = <T extends DataItemType>(
  availableColumns: Array<AvailableColumn<T>>,
  table: Table<any>
) => {
  const tableState = table.getState();
  const rows = table.getRowModel().rows;
  const visibleColumns = table.getVisibleFlatColumns().map((column) => column.id);
  const orderedVisibleIds = sortByReferenceArray(visibleColumns, tableState.columnOrder);
  const orderedColumns = orderedVisibleIds
    .map((id) => availableColumns.find((column) => column.id === id))
    .filter(Boolean) as AvailableColumn<T>[];

  const headerRow = orderedColumns.map((column) => buildCsvField(column.title)).join(',');

  const sheetRows = [
    headerRow,
    ...rows.map((row) => {
      return orderedColumns
        .map((column) => {
          const getValue = column.extractValue
            ? column.extractValue
            : accessorMap[column.type as ColumnType] ?? accessorMap.text;
          const rowValue = getValue(row.original[column.id], column);

          return typeof rowValue === 'string' ? buildCsvField(rowValue) : rowValue;
        })
        .join(',');
    }),
  ];
  return sheetRows.join('\n');
};
